import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";
import { Game } from "../game.js";
import { getMaxCampaignLevel } from "../level_config.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import type { GameScreen } from "../ui/screens/game_screen/game_screen.js";
import { PhysicsWorld } from "../physics/physics_world.js";

/**
 * A review pass over the whole level pool: step through the levels the game
 * currently plays (the active release, or the bundled table), play any of
 * them, and pick out the ones worth flagging. The picks are level NUMBERS —
 * the position in the campaign is what a designer talks about, and what a
 * replacement plan is keyed by — and they download as a plain JSON array.
 *
 * The strip also lets a level be tried WITH table spin: per table, its own
 * speed and direction, applied live to the running level so the feel can be
 * judged before anything is authored. Spins are remembered per level and go
 * into the download next to the picks, so a session's findings survive.
 *
 * The strip floats over the game so a level can be judged in play and marked
 * without leaving it; picks and spins survive reloads in localStorage.
 */
export class LevelPoolBrowser
{
  private static readonly PICKS_KEY = "bb_debug_level_picks_v1";
  private static readonly SPINS_KEY = "bb_debug_level_spins_v1";
  private static readonly POSITION_KEY = "bb_debug_level_pool_position_v1";
  private static readonly MAX_SPIN = 180;

  private static panel: HTMLDivElement | null = null;
  private static detach_resize: ( () => void ) | null = null;
  private static place: ( () => void ) | null = null;
  private static sync_timer: number | null = null;
  private static current = 1;
  private static picks: number[] = [];
  /** level → deg/sec per table index (0 = still). Only non-zero entries are kept. */
  private static spins: Record<number, number[]> = {};

  private static number_input: HTMLInputElement | null = null;
  private static pick_button: HTMLButtonElement | null = null;
  private static counter: HTMLSpanElement | null = null;
  private static header: HTMLSpanElement | null = null;
  private static spin_box: HTMLDivElement | null = null;
  private static spin_rows_for_tables = -1;
  private static spin_rows_for_level = -1;

  public static open(): void
  {
    this.picks = this.loadPicks();
    this.spins = this.loadSpins();
    // Spins ride on picks: anything left over for a level no longer listed
    // (older sessions kept them apart) is dropped rather than resurrected.
    for ( const key of Object.keys( this.spins ) )
      if ( !this.picks.includes( Number( key ) ) )
        delete this.spins[Number( key )];
    this.current = this.clamp( this.loadPosition() );
    this.ensurePanel();
    this.render();
    // Winning or losing a level moves the game on by its own rules; the strip
    // has to follow the level that is actually on screen, or "+ В СПИСОК" would
    // flag the wrong number. The screen-change event fires before the new
    // GameScreen learns its level, so a light poll is the honest way to track it.
    if ( this.sync_timer === null )
      this.sync_timer = window.setInterval( () => this.syncWithLiveScreen(), 200 );
    void this.play( this.current );
  }

  public static close(): void
  {
    if ( this.sync_timer !== null )
    {
      window.clearInterval( this.sync_timer );
      this.sync_timer = null;
    }
    this.detach_resize?.();
    this.detach_resize = null;
    this.panel?.remove();
    this.panel = null;
    this.number_input = null;
    this.pick_button = null;
    this.counter = null;
    this.header = null;
    this.spin_box = null;
    this.spin_toggle = null;
    this.spin_rows = [];
    this.spin_rows_for_tables = -1;
    this.spin_rows_for_level = -1;
  }

  public static isOpen(): boolean
  {
    return this.panel !== null;
  }

  /** Console handle: `__levelPool.screen()`, `__levelPool.spins`, `__levelPool.setSpin(t, v)`. */
  public static expose(): void
  {
    ( window as any ).__levelPool = {
      screen: () => this.liveScreen(),
      raw: () => ScreenManager.getCurrentScreenInstance(),
      name: () => ScreenManager.getCurrentScreen(),
      spins: () => this.spins,
      setSpin: ( table: number, deg_per_sec: number ) => this.setSpin( table, deg_per_sec ),
      angles: () => { const s = this.liveScreen(); return s ? Array.from( { length: s.getTableCount() }, ( _, i ) => s.getFieldRotation( i ) ) : []; },
      // A hidden tab has no rAF, so the game loop — and the physics under the
      // rotators — stands still; this ticks it by hand for headless checks.
      tick: ( steps = 60 ) => { for ( let i = 0; i < steps; i++ ) PhysicsWorld.update( 1 / 60 ); },
    };
  }

  // ---- the live game --------------------------------------------------------

  /**
   * Duck-typed on purpose: the same class can reach here through two module
   * paths in a bundle and fail `instanceof` while being a GameScreen in every
   * way that matters.
   */
  private static liveScreen(): GameScreen | null
  {
    const screen = ScreenManager.getCurrentScreenInstance() as GameScreen | null;
    return screen && typeof screen.getLevel === "function" && typeof screen.startFieldSpin === "function" ? screen : null;
  }

  private static syncWithLiveScreen(): void
  {
    if ( !this.panel )
      return;
    const screen = this.liveScreen();
    if ( !screen )
      return;

    const level = screen.getLevel();
    if ( Number.isFinite( level ) && level >= 1 && level !== this.current )
    {
      this.current = this.clamp( level );
      this.savePosition();
      this.applySpins();
      this.render();
      return;
    }

    // The table rows depend on how many tables the LIVE level has, which is
    // only known once the screen has built it — so they are (re)built here.
    const tables = screen.getTableCount();
    if ( tables !== this.spin_rows_for_tables || this.current !== this.spin_rows_for_level )
    {
      this.renderSpinRows( tables );
      this.applySpins();
    }
    else if ( this.spin_open )
    {
      for ( let table = 0; table < this.spin_rows.length; table++ )
        this.renderAngle( table );
    }
  }

  private static max(): number
  {
    return Math.max( 1, getMaxCampaignLevel() );
  }

  private static clamp( level: number ): number
  {
    return Math.min( this.max(), Math.max( 1, Math.trunc( level ) || 1 ) );
  }

  private static async play( level: number ): Promise<void>
  {
    this.current = this.clamp( level );
    this.savePosition();
    this.render();
    Game.instance.hideLoadingGameScreen();
    await ScreenManager.showScreen( Screens.GAME, { level: this.current } );
    this.render();
    // The freshly built level: rows for its tables, and its remembered spins.
    const screen = this.liveScreen();
    if ( screen )
    {
      this.renderSpinRows( screen.getTableCount() );
      this.applySpins();
    }
  }

  private static step( delta: number ): void
  {
    void this.play( this.current + delta );
  }

  // ---- picks ----------------------------------------------------------------

  private static togglePick(): void
  {
    const at = this.picks.indexOf( this.current );
    if ( at >= 0 )
    {
      this.picks.splice( at, 1 );
      // The spin belongs to the pick: a level taken off the list takes its
      // authored-for-review rotation with it, and the live tables settle.
      if ( this.spins[this.current] )
      {
        delete this.spins[this.current];
        this.saveSpins();
        this.stopLiveSpins();
        this.renderAllSpinRows();
      }
    }
    else
      this.picks.push( this.current );

    this.picks.sort( ( a, b ) => a - b );
    this.savePicks();
    this.render();
  }

  private static ensurePicked(): void
  {
    if ( this.picks.includes( this.current ) )
      return;
    this.picks.push( this.current );
    this.picks.sort( ( a, b ) => a - b );
    this.savePicks();
  }

  private static clearPicks(): void
  {
    if ( this.picks.length === 0 && Object.keys( this.spins ).length === 0 )
      return;
    if ( !window.confirm( `Очистить список (${this.picks.length} уровней) и все настройки кручения?` ) )
      return;
    this.picks = [];
    this.spins = {};
    this.savePicks();
    this.saveSpins();
    this.applySpins();
    this.render();
  }

  private static download(): void
  {
    const source = LevelPackService.isActive() ? LevelPackService.getReleaseUid() ?? "release" : "bundled";
    const picks_json = JSON.stringify( this.picks );
    // One record per picked level; `spin` is the per-table speed (deg/s, sign
    // = direction) and is present only where a spin was set. A list with no
    // spins at all stays the plain array of numbers it always was.
    const has_spins = this.picks.some( level => !!this.spins[level] );
    const payload = has_spins
      ? JSON.stringify( {
          source,
          levels: this.picks.map( level => ( { level, ...( this.spins[level] ? { spin: this.spins[level] } : {} ) } ) ),
        }, null, 2 )
      : picks_json;
    try
    {
      const blob = new Blob( [ payload + "\n" ], { type: "application/json" } );
      const url = URL.createObjectURL( blob );
      const link = document.createElement( "a" );
      link.href = url;
      link.download = `level-picks-${source}-${this.picks.length}.json`;
      link.click();
      URL.revokeObjectURL( url );
    }
    catch ( e )
    {
      console.warn( "[LevelPool] не удалось скачать список", e );
    }
    // The clipboard is the faster path on a phone, where a download may land
    // somewhere hard to find; the array is also logged so nothing is lost.
    void navigator.clipboard?.writeText( picks_json ).catch( () => {} );
    console.log( "[LevelPool] picks:", picks_json );
    if ( has_spins )
      console.log( "[LevelPool] full:", payload );
  }

  // ---- spin -----------------------------------------------------------------

  private static spinFor( table: number ): number
  {
    return this.spins[this.current]?.[table] ?? 0;
  }

  private static setSpin( table: number, deg_per_sec: number ): void
  {
    const speed = Math.max( -this.MAX_SPIN, Math.min( this.MAX_SPIN, Math.round( deg_per_sec ) ) );
    const per_table = ( this.spins[this.current] ?? [] ).slice();
    while ( per_table.length <= table )
      per_table.push( 0 );
    per_table[table] = speed;

    if ( per_table.every( s => s === 0 ) )
      delete this.spins[this.current];
    else
    {
      this.spins[this.current] = per_table;
      // A spin is a finding about this level, so the level joins the list.
      this.ensurePicked();
    }

    this.saveSpins();
    this.applySpin( table, true );
    this.renderSpinRow( table );
    this.render();
  }

  private static stopLiveSpins(): void
  {
    const screen = this.liveScreen();
    if ( !screen )
      return;
    for ( let table = 0; table < screen.getTableCount(); table++ )
      screen.stopFieldSpin( table );
  }

  private static renderAllSpinRows(): void
  {
    for ( let table = 0; table < this.spin_rows.length; table++ )
      this.renderSpinRow( table );
  }

  /**
   * A non-zero value overrides whatever the level authored; zero means "leave
   * the level's own rotation alone" — so stepping into a level that spins by
   * config shows it spinning, and only an explicit ■ press stops it.
   */
  private static applySpin( table: number, explicit_stop = false ): void
  {
    const screen = this.liveScreen();
    if ( !screen )
      return;
    const speed = this.spinFor( table );
    if ( speed !== 0 )
      screen.startFieldSpin( speed, table );
    else if ( explicit_stop )
      screen.stopFieldSpin( table );
  }

  private static applySpins(): void
  {
    const screen = this.liveScreen();
    if ( !screen )
      return;
    const tables = screen.getTableCount();
    for ( let table = 0; table < tables; table++ )
      this.applySpin( table );
  }

  private static stopAllSpins(): void
  {
    const screen = this.liveScreen();
    const tables = screen ? screen.getTableCount() : this.spins[this.current]?.length ?? 0;
    for ( let table = 0; table < tables; table++ )
      this.setSpin( table, 0 );
  }

  // ---- DOM ------------------------------------------------------------------

  private static row( ...children: HTMLElement[] ): HTMLDivElement
  {
    const div = document.createElement( "div" );
    div.style.cssText = "display:flex;align-items:center;gap:6px;justify-content:center;flex-wrap:wrap";
    for ( const child of children )
      div.appendChild( child );
    return div;
  }

  private static button( text: string, action: () => void, accent = false ): HTMLButtonElement
  {
    const btn = document.createElement( "button" );
    btn.textContent = text;
    btn.style.cssText = [
      "min-width:40px", "height:34px", "padding:0 8px", "border-radius:8px", "border:0",
      "cursor:pointer", "font:700 12px system-ui,sans-serif", "color:#0b1220", "white-space:nowrap",
      `background:${accent ? "#ffb020" : "#8fb4ff"}`,
    ].join( ";" );
    btn.onclick = ( e ) => { e.stopPropagation(); action(); };
    return btn;
  }

  private static ensurePanel(): HTMLDivElement
  {
    if ( this.panel )
      return this.panel;

    const panel = document.createElement( "div" );
    panel.style.cssText = [
      "position:fixed", "bottom:12px",
      "z-index:2147483646", "display:flex", "flex-direction:column", "gap:6px",
      "padding:8px 10px", "border-radius:12px", "background:rgba(11,18,32,.92)",
      "color:#cfe0ff", "font:600 13px/1.2 system-ui,sans-serif",
      "box-shadow:0 4px 18px rgba(0,0,0,.45)", "pointer-events:auto", "user-select:none",
      "max-width:calc(100vw - 16px)", "box-sizing:border-box",
    ].join( ";" );
    // Sit on the game field, not on the window: on a desktop the field is a
    // portrait strip at the left and the window centre is empty blue.
    const place = () =>
    {
      const field = document.getElementById( "gameUICanvas" )?.getBoundingClientRect();
      const has_field = !!field && field.width > 0;
      const centre = has_field ? field.left + field.width / 2 : window.innerWidth / 2;
      const width = has_field ? field.width : window.innerWidth;
      panel.style.left = `${Math.round( centre )}px`;
      panel.style.transform = "translateX(-50%)";
      panel.style.width = `${Math.round( width - 16 )}px`;
    };
    place();
    this.place = place;
    window.addEventListener( "resize", place );
    this.detach_resize = () => window.removeEventListener( "resize", place );

    this.header = document.createElement( "span" );
    this.header.style.cssText = "font-size:11px;opacity:.75";

    this.number_input = document.createElement( "input" );
    this.number_input.type = "number";
    this.number_input.min = "1";
    this.number_input.style.cssText = "width:64px;height:34px;border-radius:8px;border:0;text-align:center;font:700 14px system-ui,sans-serif";
    this.number_input.onchange = () => void this.play( Number( this.number_input!.value ) );
    this.number_input.onkeydown = ( e ) => { if ( e.key === "Enter" ) this.number_input!.blur(); e.stopPropagation(); };

    this.pick_button = this.button( "+ В СПИСОК", () => this.togglePick(), true );
    this.pick_button.title = "Уровень в список; кручение столов сохраняется вместе с ним";

    this.counter = document.createElement( "span" );
    this.counter.style.cssText = "min-width:70px;text-align:center";

    this.spin_box = document.createElement( "div" );
    this.spin_box.style.cssText = "display:flex;flex-direction:column;gap:4px;padding-top:4px;border-top:1px solid rgba(207,224,255,.15)";

    panel.appendChild( this.row( this.header ) );
    panel.appendChild( this.row(
      this.button( "◀", () => this.step( -1 ) ),
      this.number_input,
      this.button( "▶", () => this.step( 1 ) ),
      this.button( "▶▶ +10", () => this.step( 10 ) ),
    ) );
    this.spin_toggle = this.button( "⟳ КРУЧЕНИЕ ▾", () => this.toggleSpinBox() );

    panel.appendChild( this.row(
      this.pick_button,
      this.counter,
      this.button( "⬇ СКАЧАТЬ", () => this.download() ),
      this.button( "✕ СПИСОК", () => this.clearPicks() ),
      this.spin_toggle,
      this.button( "ЗАКРЫТЬ", () => this.close() ),
    ) );
    panel.appendChild( this.spin_box );
    this.spin_box.style.display = this.spin_open ? "" : "none";

    document.body.appendChild( panel );
    this.panel = panel;
    return panel;
  }

  private static spin_rows: { slider: HTMLInputElement; value: HTMLInputElement; angle: HTMLSpanElement }[] = [];
  private static spin_open = false;
  private static spin_toggle: HTMLButtonElement | null = null;

  private static renderSpinRows( tables: number ): void
  {
    if ( !this.spin_box )
      return;
    this.spin_rows_for_tables = tables;
    this.spin_rows_for_level = this.current;
    this.spin_box.innerHTML = "";
    this.spin_rows = [];

    const title = document.createElement( "span" );
    title.style.cssText = "font-size:11px;opacity:.75";
    title.textContent = tables > 0
      ? `Кручение (°/с, − = против часовой) · столов: ${tables}`
      : "Кручение — уровень ещё грузится…";
    this.spin_box.appendChild( this.row( title, ...( tables > 1 ? [ this.button( "■ СТОП ВСЕ", () => this.stopAllSpins() ) ] : [] ) ) );

    for ( let table = 0; table < tables; table++ )
    {
      const label = document.createElement( "span" );
      label.style.cssText = "min-width:48px;font-size:12px";
      label.textContent = tables > 1 ? `Стол ${table + 1}` : "Стол";

      const slider = document.createElement( "input" );
      slider.type = "range";
      slider.min = String( -this.MAX_SPIN );
      slider.max = String( this.MAX_SPIN );
      slider.step = "5";
      slider.style.cssText = "flex:1;min-width:90px;max-width:160px";
      slider.oninput = () => this.setSpin( table, Number( slider.value ) );

      const value = document.createElement( "input" );
      value.type = "number";
      value.min = String( -this.MAX_SPIN );
      value.max = String( this.MAX_SPIN );
      value.step = "5";
      value.style.cssText = "width:58px;height:30px;border-radius:8px;border:0;text-align:center;font:700 13px system-ui,sans-serif";
      value.onchange = () => this.setSpin( table, Number( value.value ) );
      value.onkeydown = ( e ) => { if ( e.key === "Enter" ) value.blur(); e.stopPropagation(); };

      const ccw = this.button( "⟲", () => this.setSpin( table, this.spinFor( table ) - 15 ) );
      const cw = this.button( "⟳", () => this.setSpin( table, this.spinFor( table ) + 15 ) );
      const stop = this.button( "■", () => this.setSpin( table, 0 ) );

      // Live angle: the proof that the table is actually turning, and how far.
      const angle = document.createElement( "span" );
      angle.style.cssText = "min-width:42px;font-size:11px;opacity:.75;text-align:right;font-variant-numeric:tabular-nums";

      this.spin_rows.push( { slider, value, angle } );
      this.spin_box.appendChild( this.row( label, ccw, slider, cw, value, stop, angle ) );
      this.renderSpinRow( table );
    }
    this.spin_box.style.display = this.spin_open ? "" : "none";
  }

  private static renderSpinRow( table: number ): void
  {
    const row = this.spin_rows[table];
    if ( !row )
      return;
    const speed = this.spinFor( table );
    row.slider.value = String( speed );
    row.value.value = String( speed );
    row.value.style.background = speed === 0 ? "#fff" : speed > 0 ? "#bfe6ff" : "#ffd9bf";
    this.renderAngle( table );
  }

  private static renderAngle( table: number ): void
  {
    const row = this.spin_rows[table];
    const screen = this.liveScreen();
    if ( !row || !screen )
      return;
    const deg = screen.getFieldRotation( table );
    row.angle.textContent = `${Math.round( ( ( deg % 360 ) + 360 ) % 360 )}°`;
  }

  private static toggleSpinBox(): void
  {
    this.spin_open = !this.spin_open;
    if ( this.spin_box )
      this.spin_box.style.display = this.spin_open ? "" : "none";
    if ( this.spin_toggle )
    {
      this.spin_toggle.textContent = this.spin_open ? "⟳ КРУЧЕНИЕ ▴" : "⟳ КРУЧЕНИЕ ▾";
      this.spin_toggle.style.background = this.spin_open ? "#3ecf8e" : "#8fb4ff";
    }
  }

  private static render(): void
  {
    if ( !this.panel )
      return;

    // The field canvas is laid out lazily; re-anchor on every render so the
    // strip is right even when it was created before the canvas had a size.
    this.place?.();

    const picked = this.picks.includes( this.current );
    const source = LevelPackService.isActive() ? `релиз ${LevelPackService.getReleaseUid() ?? ""}` : "bundled";
    const spun = Object.keys( this.spins ).length;

    if ( this.header )
      this.header.textContent = `Пул: ${source} · ${this.max()} уровней${spun ? ` · с кручением: ${spun}` : ""}`;
    if ( this.number_input )
    {
      this.number_input.max = String( this.max() );
      this.number_input.value = String( this.current );
    }
    if ( this.pick_button )
    {
      this.pick_button.textContent = picked ? "✓ В СПИСКЕ" : "+ В СПИСОК";
      this.pick_button.style.background = picked ? "#3ecf8e" : "#ffb020";
    }
    if ( this.counter )
      this.counter.textContent = `в списке: ${this.picks.length}`;
    for ( let table = 0; table < this.spin_rows.length; table++ )
      this.renderSpinRow( table );
  }

  // ---- storage --------------------------------------------------------------

  private static loadPicks(): number[]
  {
    try
    {
      const raw = JSON.parse( localStorage.getItem( this.PICKS_KEY ) ?? "[]" );
      return Array.isArray( raw ) ? raw.map( Number ).filter( n => Number.isInteger( n ) && n > 0 ) : [];
    }
    catch
    {
      return [];
    }
  }

  private static savePicks(): void
  {
    try { localStorage.setItem( this.PICKS_KEY, JSON.stringify( this.picks ) ); } catch { /* storage full or blocked — the list still lives in memory */ }
  }

  private static loadSpins(): Record<number, number[]>
  {
    try
    {
      const raw = JSON.parse( localStorage.getItem( this.SPINS_KEY ) ?? "{}" );
      const out: Record<number, number[]> = {};
      if ( raw && typeof raw === "object" )
      {
        for ( const [ key, value ] of Object.entries( raw as Record<string, unknown> ) )
        {
          const level = Number( key );
          if ( Number.isInteger( level ) && level > 0 && Array.isArray( value ) )
            out[level] = value.map( v => Number( v ) || 0 );
        }
      }
      return out;
    }
    catch
    {
      return {};
    }
  }

  private static saveSpins(): void
  {
    try { localStorage.setItem( this.SPINS_KEY, JSON.stringify( this.spins ) ); } catch { /* same as above */ }
  }

  private static loadPosition(): number
  {
    return Number( localStorage.getItem( this.POSITION_KEY ) ) || 1;
  }

  private static savePosition(): void
  {
    try { localStorage.setItem( this.POSITION_KEY, String( this.current ) ); } catch { /* same as above */ }
  }
}
